import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2, AlertTriangle, X, Save, ChefHat, Route as RouteIcon } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type Station = { id: string; name: string; code: string | null; type: string; isActive: boolean };
type Route = { id: string; productId: string | null; categoryId: string | null; stationId: string; priority: number; isActive: boolean };
type Location = { id: string; name: string };
type RoutingTargets = { products: Array<{ id: string; name: string }>; categories: Array<{ id: string; name: string }> };

export function LocationKitchenPage() {
  const { id: locationId } = useParams<{ id: string }>();
  const [location, setLocation] = useState<Location | null>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [targets, setTargets] = useState<RoutingTargets>({ products: [], categories: [] });
  const [error, setError] = useState<string | null>(null);
  const [creatingStation, setCreatingStation] = useState(false);
  const [creatingRoute, setCreatingRoute] = useState(false);
  const [deleteStationTarget, setDeleteStationTarget] = useState<Station | null>(null);
  const [deleteRouteTarget, setDeleteRouteTarget] = useState<Route | null>(null);

  const refresh = useCallback(async () => {
    if (!locationId) return;
    try {
      const [loc, s, r, t] = await Promise.all([
        api.get<Location>(`/admin/locations/${locationId}`),
        api.get<Station[]>(`/admin/locations/${locationId}/kitchen-stations`),
        api.get<Route[]>(`/admin/locations/${locationId}/kitchen-routes`),
        api.get<RoutingTargets>(`/admin/locations/${locationId}/routing-targets`),
      ]);
      setLocation(loc);
      setStations(s);
      setRoutes(r);
      setTargets(t);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  }, [locationId]);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmDeleteStation = async () => {
    if (!deleteStationTarget) return;
    await api.del(`/admin/locations/${locationId}/kitchen-stations/${deleteStationTarget.id}`);
    refresh();
  };

  const confirmDeleteRoute = async () => {
    if (!deleteRouteTarget) return;
    await api.del(`/admin/locations/${locationId}/kitchen-routes/${deleteRouteTarget.id}`);
    refresh();
  };

  return (
    <div>
      <Link to="/locations" style={backLink}><ChevronLeft size={14} /> Lokacije</Link>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            <ChefHat size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Kuhinja: {location?.name || '...'}
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Stanice i routing pravila za KDS
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreatingStation(true)} style={btnPrimary}>
          <Plus size={14} /> Nova stanica
        </button>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      <h3 style={sectionTitle}>Stanice ({stations.length})</h3>
      <div style={card}>
        {stations.length === 0 ? (
          <div style={emptyState}>
            <ChefHat size={32} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
            <div>Nema stanica</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Naziv</th><th style={th}>Code</th><th style={th}>Tip</th><th style={th}>Status</th>
                <th style={{ ...th, width: 80, textAlign: 'right' }}>Akcije</th>
              </tr></thead>
              <tbody>
                {stations.map((s) => (
                  <tr key={s.id}>
                    <td style={{ ...td, fontWeight: 600 }}>{s.name}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, color: 'var(--color-text-muted)' }}>{s.code || '-'}</td>
                    <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{s.type}</td>
                    <td style={td}>{s.isActive ? <span style={badgeActive}>Aktivna</span> : <span style={badgeInactive}>Neaktivna</span>}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => setDeleteStationTarget(s)} style={{ ...iconBtn, color: '#fca5a5' }}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <h3 style={{ ...sectionTitle, margin: 0 }}>Routing pravila ({routes.length})</h3>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setCreatingRoute(true)}
          disabled={stations.length === 0}
          style={{ ...btnPrimary, padding: '6px 12px', fontSize: 12, opacity: stations.length === 0 ? 0.4 : 1 }}
          title={stations.length === 0 ? 'Prvo kreiraj bar jednu stanicu' : 'Dodaj routing pravilo'}
        >
          <Plus size={13} /> Novo pravilo
        </button>
      </div>
      <div style={{ ...card, marginTop: 12 }}>
        {routes.length === 0 ? (
          <div style={emptyState}>
            <RouteIcon size={32} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
            <div>Nema routing pravila - sve stavke idu na default (prvu) stanicu</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Tip</th>
                <th style={th}>Target</th>
                <th style={th}>Stanica</th>
                <th style={{ ...th, textAlign: 'right' }}>Priority</th>
                <th style={th}>Status</th>
                <th style={{ ...th, width: 80, textAlign: 'right' }}>Akcije</th>
              </tr></thead>
              <tbody>
                {routes.map((r) => {
                  const stationName = stations.find((s) => s.id === r.stationId)?.name || '-';
                  const targetName = r.productId
                    ? targets.products.find((p) => p.id === r.productId)?.name || `(obrisan proizvod ${r.productId.slice(0, 8)})`
                    : r.categoryId
                      ? targets.categories.find((c) => c.id === r.categoryId)?.name || `(obrisana kat. ${r.categoryId.slice(0, 8)})`
                      : '-';
                  return (
                    <tr key={r.id}>
                      <td style={td}>
                        <span style={{ ...badgeRule, background: r.productId ? 'rgba(59,130,246,0.15)' : 'rgba(168,85,247,0.15)', color: r.productId ? '#93c5fd' : '#c4b5fd' }}>
                          {r.productId ? 'Proizvod' : 'Kategorija'}
                        </span>
                      </td>
                      <td style={{ ...td, fontWeight: 500 }}>{targetName}</td>
                      <td style={td}>{stationName}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.priority}</td>
                      <td style={td}>{r.isActive ? <span style={badgeActive}>Aktivno</span> : <span style={badgeInactive}>Pauzirano</span>}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button onClick={() => setDeleteRouteTarget(r)} style={{ ...iconBtn, color: '#fca5a5' }} title="Obriši">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, padding: 12, background: 'var(--color-bg-surface)', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 12, color: 'var(--color-text-muted)' }}>
        <strong style={{ color: 'var(--color-text)' }}>Kako radi:</strong> Svaka stavka narudžbine prolazi kroz routing pravila - prvo se traži match po <em>proizvodu</em>, pa po <em>kategoriji</em>. Ako više pravila poklopi istu stavku, pobednik je ono sa <strong>nižim priority brojem</strong> (default 100). Ako nijedno pravilo ne pogodi, stavka ide na prvu aktivnu stanicu.
      </div>

      {creatingStation && <StationFormDialog locationId={locationId!} onClose={() => setCreatingStation(false)} onSaved={() => { setCreatingStation(false); refresh(); }} />}
      {creatingRoute && (
        <RouteFormDialog
          locationId={locationId!}
          stations={stations}
          targets={targets}
          onClose={() => setCreatingRoute(false)}
          onSaved={() => { setCreatingRoute(false); refresh(); }}
        />
      )}

      {deleteStationTarget && (
        <ConfirmDialog
          title="Obriši stanicu"
          message={`Da li želite da obrišete stanicu "${deleteStationTarget.name}"? Ako ima istoriju tiketa, brisanje neće proći.`}
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={confirmDeleteStation}
          onClose={() => setDeleteStationTarget(null)}
        />
      )}

      {deleteRouteTarget && (
        <ConfirmDialog
          title="Obriši routing pravilo"
          message="Da li želite da obrišete ovo routing pravilo?"
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={confirmDeleteRoute}
          onClose={() => setDeleteRouteTarget(null)}
        />
      )}
    </div>
  );
}

function StationFormDialog({ locationId, onClose, onSaved }: { locationId: string; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [type, setType] = useState('kitchen');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Naziv obavezan'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/locations/${locationId}/kitchen-stations`, { name: name.trim(), code: code.trim() || undefined, type });
      onSaved();
    } catch (e: any) { setError(e?.data?.message || e?.message || 'Greška'); }
    finally { setSaving(false); }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>Nova stanica</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Naziv *">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Topla kuhinja, Bar..." autoFocus required style={input} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Code">
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="HOT, BAR..." style={{ ...input, fontFamily: 'monospace' }} />
            </Field>
            <Field label="Tip *">
              <select value={type} onChange={(e) => setType(e.target.value)} style={input}>
                <option value="kitchen">Kuhinja</option>
                <option value="bar">Bar</option>
                <option value="dessert">Poslastičarnica</option>
                <option value="grill">Roštilj</option>
                <option value="pizza">Pica</option>
                <option value="other">Ostalo</option>
              </select>
            </Field>
          </div>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}><Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RouteFormDialog({
  locationId,
  stations,
  targets,
  onClose,
  onSaved,
}: {
  locationId: string;
  stations: Station[];
  targets: RoutingTargets;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const [targetType, setTargetType] = useState<'product' | 'category'>('category');
  const [productId, setProductId] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>(targets.categories[0]?.id || '');
  const [stationId, setStationId] = useState<string>(stations.find((s) => s.isActive)?.id || stations[0]?.id || '');
  const [priority, setPriority] = useState(100);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stationId) { setError('Izaberi stanicu'); return; }
    if (targetType === 'product' && !productId) { setError('Izaberi proizvod'); return; }
    if (targetType === 'category' && !categoryId) { setError('Izaberi kategoriju'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/locations/${locationId}/kitchen-routes`, {
        productId: targetType === 'product' ? productId : undefined,
        categoryId: targetType === 'category' ? categoryId : undefined,
        stationId,
        priority,
        isActive: true,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>Novo routing pravilo</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Tip targeta">
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ ...radioLabel, borderColor: targetType === 'category' ? 'var(--color-primary)' : 'var(--color-border)' }}>
                <input
                  type="radio"
                  value="category"
                  checked={targetType === 'category'}
                  onChange={() => setTargetType('category')}
                  style={{ marginRight: 6 }}
                />
                Kategorija
              </label>
              <label style={{ ...radioLabel, borderColor: targetType === 'product' ? 'var(--color-primary)' : 'var(--color-border)' }}>
                <input
                  type="radio"
                  value="product"
                  checked={targetType === 'product'}
                  onChange={() => setTargetType('product')}
                  style={{ marginRight: 6 }}
                />
                Pojedinačan proizvod
              </label>
            </div>
          </Field>

          {targetType === 'category' ? (
            <Field label="Kategorija *">
              {targets.categories.length === 0 ? (
                <div style={{ padding: 10, fontSize: 12, color: '#fca5a5', background: 'rgba(239,68,68,0.08)', borderRadius: 6 }}>
                  Nema kategorija za ovaj tenant. Dodaj kategorije kroz POS katalog.
                </div>
              ) : (
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required style={input}>
                  {targets.categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
            </Field>
          ) : (
            <Field label="Proizvod *">
              {targets.products.length === 0 ? (
                <div style={{ padding: 10, fontSize: 12, color: '#fca5a5', background: 'rgba(239,68,68,0.08)', borderRadius: 6 }}>
                  Nema proizvoda za ovaj tenant.
                </div>
              ) : (
                <select value={productId} onChange={(e) => setProductId(e.target.value)} required style={input}>
                  <option value="">- izaberi -</option>
                  {targets.products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
            <Field label="Stanica *">
              <select value={stationId} onChange={(e) => setStationId(e.target.value)} required style={input}>
                {stations.map((s) => (
                  <option key={s.id} value={s.id} disabled={!s.isActive}>
                    {s.name} {s.isActive ? '' : '(neaktivna)'}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Priority">
              <input
                type="number"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value, 10) || 100)}
                min={1}
                max={999}
                style={input}
                title="Niža vrednost = više prioriteta. Default 100."
              />
            </Field>
          </div>

          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', padding: '4px 2px' }}>
            Priority određuje redosled match-ovanja. Pravilo sa nižim priority brojem prvo pogađa. Koristi 50 za specifična override, 100 za default, 200+ za fallback.
          </div>

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>{children}</div>;
}

const backLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)', fontSize: 12, marginBottom: 12, textDecoration: 'none' };
const sectionTitle: React.CSSProperties = { margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const emptyState: React.CSSProperties = { padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' };
const th: React.CSSProperties = { padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border-light)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 480, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
const badgeActive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', fontWeight: 700 };
const badgeInactive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', fontWeight: 700 };
const badgeRule: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 700 };
const radioLabel: React.CSSProperties = { flex: 1, padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, color: 'var(--color-text)', cursor: 'pointer', display: 'flex', alignItems: 'center' };
